import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Icons } from '@/components/ui/icons';
import { Card, CardContent } from '@/components/ui/card';
import { ServiceAreasTable } from '@/components/features/service-areas/service-areas-table';
import { ServiceAreaModal } from '@/components/features/service-areas/service-area-modal';
import { ServiceAreaFilters, type FilterState } from '@/components/features/service-areas/service-area-filters';
import { useTranslation } from 'react-i18next';

const ServiceAreasPage = () => {
  const { t } = useTranslation('operator');
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    kvRegion: '',
  });
  const [serviceAreaModalOpen, setServiceAreaModalOpen] = useState(false);

  // Query service areas with filters
  const queryArgs = useMemo(() => {
    const args: {
      kvRegion?: string;
    } = {};

    if (filters.kvRegion) {
      args.kvRegion = filters.kvRegion;
    }

    return args;
  }, [filters.kvRegion]);

  const serviceAreas = useQuery(api.app.serviceAreas.listServiceAreas, queryArgs);

  // Client-side search filter
  const filteredServiceAreas = useMemo(() => {
    if (!serviceAreas) return [];

    if (!filters.search) return serviceAreas;

    const searchLower = filters.search.toLowerCase();
    return serviceAreas.filter((area: (typeof serviceAreas)[number]) => {
      return area.name.toLowerCase().includes(searchLower) || area.kvRegion.toLowerCase().includes(searchLower);
    });
  }, [serviceAreas, filters.search]);

  const isLoading = serviceAreas === undefined;

  return (
    <div className='p-6 space-y-6' data-testid='service-areas-page'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <div>
            <h1 className='text-2xl font-semibold'>{t('serviceAreas.title')}</h1>
            <p className='text-sm text-muted-foreground mt-1'>{t('serviceAreas.subtitle')}</p>
          </div>
          {!isLoading && (
            <Badge variant='secondary' className='ml-2 tabular-nums'>
              {filteredServiceAreas.length}
            </Badge>
          )}
        </div>
        <Button onClick={() => setServiceAreaModalOpen(true)} data-testid='service-areas-create'>
          <Icons.Plus className='h-4 w-4 mr-2' />
          {t('serviceAreas.create')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className='pt-4 pb-4'>
          <ServiceAreaFilters filters={filters} onChange={setFilters} />
        </CardContent>
      </Card>

      {/* Table */}
      <ServiceAreasTable serviceAreas={filteredServiceAreas} isLoading={isLoading} />

      {/* Create Modal */}
      <ServiceAreaModal open={serviceAreaModalOpen} onOpenChange={setServiceAreaModalOpen} serviceArea={null} />
    </div>
  );
};

export const Route = createFileRoute('/_protected/operators/service-areas')({
  component: ServiceAreasPage,
});
